import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiBarChart2, FiUser } from 'react-icons/fi';
import api from '../../utils/api';
import { PERMISSIONS, buildRoleFeatureMatrix, canFeatureForRole } from '../../utils/permissions';
import {
  ABANDONED_FEEDBACK_MARKER,
  isAbandonedSupervisorFeedback,
  isAbandonedSupervisorRatingEntry,
} from '../../utils/matchFeedbackAbandoned';
import { formatMatchDayPillLabel } from '../../utils/matchFeedbackSchedule';
import { formatDateTime } from '../../utils/feedbackViewFormat';
import {
  FEEDBACK_SUMMARY_CARD_OUTER,
  FEEDBACK_TABLE_HEAD_GRADIENT,
  metricScoreBadge,
  SUMMARY_DETAIL_ITEM,
  SUMMARY_DETAIL_LABEL,
  SUMMARY_DETAIL_VALUE_SMALL,
  SUMMARY_DETAILS_GRID,
} from '../../utils/feedbackViewSharedUi';
import { isDaysMatchType, resolveFeedbackDayNumber as resolveDayNumber } from '../../utils/matchTypeUi';
import {
  getRoleIconComponent,
  TEAM_MEMBER_MODAL_ACCENT,
} from '../../utils/teamMemberDetailModalUi';

const collectSupervisorRecords = (payload) => {
  const raw = payload?.data || payload;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (Array.isArray(raw?.feedbacks)) return raw.feedbacks.filter(Boolean);
  if (Array.isArray(raw?.records)) return raw.records.filter(Boolean);
  if (Array.isArray(raw?.data)) return raw.data.filter(Boolean);
  if (Array.isArray(raw?.docs)) return raw.docs.filter(Boolean);
  if (Array.isArray(raw?.results)) return raw.results.filter(Boolean);
  if (Array.isArray(raw?.items)) return raw.items.filter(Boolean);
  if (Array.isArray(raw?.rows)) return raw.rows.filter(Boolean);
  if (Array.isArray(raw?.data?.data)) return raw.data.data.filter(Boolean);
  if (raw && typeof raw === 'object') return [raw];
  return [];
};

const EMPTY_RATINGS = [];

const mergeUniqueSupervisorRecords = (records = []) => {
  const merged = [];
  records.forEach((record) => {
    if (!record || typeof record !== 'object') return;
    const rid = String(record?._id || '').trim();
    const rday = resolveDayNumber(record);
    const exists = merged.some((item) => {
      const iid = String(item?._id || '').trim();
      if (rid && iid) return rid === iid;
      return rday != null && resolveDayNumber(item) === rday;
    });
    if (!exists) merged.push(record);
  });
  return merged.sort((a, b) => (resolveDayNumber(a) || 0) - (resolveDayNumber(b) || 0));
};

const SupervisorFeedbackViewPage = () => {
  const { matchId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const appBasePath = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const [loading, setLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');

  useEffect(() => {
    if (!matchId) return;
    const load = async () => {
      setLoading(true);
      try {
        let matrix = {};
        try {
          const permResponse = await api.getPermissions();
          const permissionRows = Array.isArray(permResponse?.data)
            ? permResponse.data
            : Array.isArray(permResponse?.data?.permissions)
            ? permResponse.data.permissions
            : Array.isArray(permResponse?.permissions)
            ? permResponse.permissions
            : [];
          matrix = buildRoleFeatureMatrix(permissionRows);
        } catch {
          matrix = {};
        }
        let user = {};
        try {
          user = JSON.parse(sessionStorage.getItem('user') || '{}');
        } catch {
          user = {};
        }
        if (!canFeatureForRole(user?.role, matrix, [PERMISSIONS.VIEW_SUPERVISOR_FEEDBACK])) {
          toast.error('You do not have permission to view supervisor feedback');
          navigate(`${appBasePath}/matches`);
          setLoading(false);
          return;
        }

        const res = await api.getSupervisorFeedbackByMatchId(matchId);
        // { success: true, data: { ratings: [...], matchId: {...}, ... } }
        const data = (res?.success && res?.data) ? res.data : (res?.data || res);
        // Endpoint is already scoped by matchId: /supervisor-feedback/match/:matchId
        // Keep response as-is (like TL feedback view flow) and only de-duplicate.
        const recordsFromMatchEndpoint = collectSupervisorRecords(data);
        setFeedbackData(mergeUniqueSupervisorRecords(recordsFromMatchEndpoint));
      } catch (err) {
        toast.error(err?.status === 404 ? 'No supervisor feedback found' : (err.message || 'Failed to load'));
        navigate(`${appBasePath}/matches`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [matchId, navigate, appBasePath]);

  const matchTypeFromState = location.state?.matchType;
  const baseRecord = feedbackData[0] || {};
  const match = baseRecord.matchId || {};
  const isDaysMatch = isDaysMatchType(matchTypeFromState || match?.matchType || baseRecord?.matchType);
  const dayOptions = useMemo(
    () => Array.from(new Set(feedbackData.map((item) => resolveDayNumber(item)).filter(Boolean))).sort((a, b) => a - b),
    [feedbackData]
  );
  const showDayTabs = isDaysMatch && dayOptions.length > 0;

  useEffect(() => {
    if (!showDayTabs || dayOptions.length === 0) return;
    const selected = Number(selectedDay);
    if (!selectedDay || !dayOptions.includes(selected)) {
      setSelectedDay(String(dayOptions[0]));
    }
  }, [showDayTabs, dayOptions, selectedDay]);

  const resolvedDayNumber =
    showDayTabs && dayOptions.length > 0
      ? dayOptions.includes(Number(selectedDay))
        ? Number(selectedDay)
        : dayOptions[0]
      : null;
  const activeRecord =
    showDayTabs && resolvedDayNumber != null
      ? feedbackData.find((item) => resolveDayNumber(item) === resolvedDayNumber) || baseRecord
      : baseRecord;

  // Response shape: { matchId: {...}, eventTeamId: {...}, ratings: [...], evaluator: {...}, averageRating, overallFeedback, ... }
  const team = activeRecord.eventTeamId || match.eventTeam || {};
  const ratings = useMemo(
    () => (Array.isArray(activeRecord.ratings) ? activeRecord.ratings : EMPTY_RATINGS),
    [activeRecord]
  );
  const evaluatorName = activeRecord.evaluator?.name || activeRecord.evaluator?.fullName ||
    (typeof activeRecord.evaluator === 'string' ? activeRecord.evaluator : '');
  const overallFeedbackText = String(activeRecord?.overallFeedback ?? '').trim();
  const isViewAbandoned = useMemo(
    () => isAbandonedSupervisorFeedback(activeRecord),
    [activeRecord]
  );
  const quickSummary = useMemo(() => {
    if (!ratings.length || isViewAbandoned) return null;
    const values = ratings
      .filter((entry) => !isAbandonedSupervisorRatingEntry(entry))
      .map((entry) => Number(entry?.rating))
      .filter((num) => Number.isFinite(num));
    if (!values.length) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }, [ratings, isViewAbandoned]);

  if (loading) {
    return (
      <div style={{ padding: '20px', background: '#eef2f6', minHeight: '100%' }}>
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(15, 23, 42, 0.07)',
            border: '1px solid #e5e7eb',
            padding: '40px',
            textAlign: 'center',
            color: '#98a2b3',
            fontSize: '13px',
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (!feedbackData.length) return null;

  const teamNameDisplay = team?.teamName || team?.name || '—';
  const venueDisplay = activeRecord.venue || match.matchLocation || '—';
  const summaryAverage =
    isViewAbandoned
      ? 'NA'
      : Number.isFinite(Number(activeRecord.averageRating))
        ? `${Number(activeRecord.averageRating).toFixed(1)} / 5.0`
        : '—';

  return (
    <div style={{ padding: '20px', background: '#eef2f6', minHeight: '100%' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(15, 23, 42, 0.07)',
          border: '1px solid #e5e7eb',
          padding: '22px 24px 26px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px',
            marginBottom: '22px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', minWidth: 0 }}>
            <div
              aria-hidden
              style={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: 'rgba(102, 126, 234, 0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#667eea',
              }}
            >
              <FiBarChart2 size={24} strokeWidth={2} />
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e1b4b',
                  letterSpacing: '-0.02em',
                }}
              >
                Supervisor Feedback
              </h1>
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: '13px',
                  color: '#64748b',
                  lineHeight: 1.45,
                  maxWidth: '540px',
                }}
              >
                Supervisor ratings by role and team member.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/matches')}
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '34px',
              padding: '0 14px',
              border: '1px solid #d0d5dd',
              background: '#ffffff',
              color: '#1e293b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <FiArrowLeft size={14} strokeWidth={2.2} aria-hidden />
            Back
          </button>
        </div>

        <div style={FEEDBACK_SUMMARY_CARD_OUTER} role="region" aria-label="Match summary">
          <div style={SUMMARY_DETAILS_GRID}>
            <div style={SUMMARY_DETAIL_ITEM}>
              <div>
                <p style={SUMMARY_DETAIL_LABEL}>Match Name</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>{match.matchName || '—'}</p>
              </div>
              <div style={{ marginTop: '14px' }}>
                <p style={SUMMARY_DETAIL_LABEL}>Average Rating</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>{summaryAverage}</p>
              </div>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <div>
                <p style={SUMMARY_DETAIL_LABEL}>Venue</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>{venueDisplay}</p>
              </div>
              <div style={{ marginTop: '14px' }}>
                <p style={SUMMARY_DETAIL_LABEL}>Evaluator</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>{evaluatorName || '—'}</p>
              </div>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <p style={SUMMARY_DETAIL_LABEL}>Team</p>
              <p style={SUMMARY_DETAIL_VALUE_SMALL}>{teamNameDisplay}</p>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <p style={SUMMARY_DETAIL_LABEL}>Start Date & Time</p>
              <p style={SUMMARY_DETAIL_VALUE_SMALL}>
                {formatDateTime(activeRecord.startDate || match.startDate, activeRecord.startTime || match.startTime)}
              </p>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <p style={SUMMARY_DETAIL_LABEL}>End Date & Time</p>
              <p style={SUMMARY_DETAIL_VALUE_SMALL}>
                {formatDateTime(activeRecord.endDate || match.endDate, activeRecord.endTime || match.endTime)}
              </p>
            </div>
          </div>
        </div>

        {/* Ratings Table */}
        {showDayTabs && dayOptions.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '2px', marginBottom: '10px' }}>
            {dayOptions.map((day) => {
              const isSelected = resolvedDayNumber === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(String(day))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '999px',
                    border: isSelected ? '1px solid #2563eb' : '1px solid #d0d5dd',
                    background: isSelected ? '#2563eb' : '#f8fafc',
                    color: isSelected ? 'white' : '#475467',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatMatchDayPillLabel(day, match.startDate || match.date || activeRecord.startDate)}
                </button>
              );
            })}
          </div>
        )}
        {ratings.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', padding: '32px 0' }}>No ratings found.</div>
        ) : (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={FEEDBACK_TABLE_HEAD_GRADIENT}>
                  <tr>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '52px',
                      }}
                    >
                      S.No
                    </th>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      Job Type
                    </th>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      Member
                    </th>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '100px',
                      }}
                    >
                      Rating
                    </th>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ratings.map((entry, idx) => {
                    const rowEntryAbandoned = isViewAbandoned || isAbandonedSupervisorRatingEntry(entry);
                    const ratingText = rowEntryAbandoned
                      ? 'NA'
                      : Number.isFinite(Number(entry?.rating))
                        ? Number(entry.rating).toFixed(1)
                        : 'NA';
                    const { bg, color } = metricScoreBadge(rowEntryAbandoned ? null : entry?.rating);
                    const remarkDisplay =
                      String(entry?.remarks ?? '').trim().toUpperCase() === String(ABANDONED_FEEDBACK_MARKER).toUpperCase()
                        ? ABANDONED_FEEDBACK_MARKER
                        : entry.remarks || '-';
                    const empKey = entry.employeeId?._id ?? entry.employeeId?.id ?? idx;
                    const RoleIcon = getRoleIconComponent(entry.role);
                    return (
                      <tr key={`${entry.role || ''}-${empKey}-${idx}`} style={{ borderTop: '1px solid #e5e7eb', background: '#ffffff' }}>
                        <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            <RoleIcon size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                            {entry.role || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            <FiUser size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                            {entry.employeeId?.name || entry.employeeId?.fullName || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              minWidth: '36px',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              background: bg,
                              color,
                              fontSize: '12px',
                              fontWeight: 700,
                            }}
                          >
                            {ratingText}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '12px', color: '#667085', verticalAlign: 'middle' }}>{remarkDisplay}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Overall Feedback + Quick Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr', gap: '12px', marginTop: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#667085', fontWeight: 700, marginBottom: '6px' }}>
              OVERALL FEEDBACK
            </label>
            <div
              role="region"
              aria-label="Overall feedback"
              tabIndex={-1}
              style={{
                minHeight: '96px',
                width: '100%',
                padding: '10px',
                boxSizing: 'border-box',
                border: '1px solid #eaecf0',
                borderRadius: '12px',
                fontSize: '13px',
                fontFamily: 'inherit',
                lineHeight: 1.6,
                background: '#f8fafc',
                color: overallFeedbackText ? '#344054' : '#98a2b3',
                cursor: 'default',
                pointerEvents: overallFeedbackText ? 'auto' : 'none',
                userSelect: overallFeedbackText ? 'text' : 'none',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {overallFeedbackText || 'No feedback'}
            </div>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ border: '1px dashed #d0d5dd', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: '#667085', fontWeight: 700 }}>QUICK SUMMARY</div>
              <div style={{ marginTop: '4px', fontSize: '38px', lineHeight: 1, fontWeight: 700, color: '#101828' }}>
                {quickSummary == null ? (isViewAbandoned ? 'N/A' : '—') : quickSummary.toFixed(1)}
              </div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#667085' }}>Average</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupervisorFeedbackViewPage;
